"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-normal break-words font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        // y-deep: the darkened brand yellow, so filled buttons have enough
        // weight to read as clickable. The general `y` yellow is unchanged.
        default:
          "bg-y-deep text-black hover:bg-y-deep-hover font-bold uppercase tracking-wider no-white-force active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98]",
        outline:
          "border border-[var(--rule)] bg-background hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline",
        danger:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98]",
        warning:
          "bg-warning text-warning-foreground hover:bg-warning/90 active:scale-[0.98]",
        success:
          "bg-success text-success-foreground hover:bg-success/90 active:scale-[0.98]",
        text: "hover:bg-transparent hover:text-accent-foreground underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-7 px-2 text-xs rounded-md",
        sm: "h-8 px-3 text-xs rounded-md",
        md: "h-10 px-4 text-sm rounded-md",
        lg: "h-11 px-6 text-base rounded-md",
        xl: "h-12 px-8 text-base rounded-md",
        icon: "h-10 w-10 p-0 rounded-md",
        iconSm: "h-8 w-8 p-0 rounded-md",
        iconLg: "h-12 w-12 p-0 rounded-md",
      },
      width: {
        auto: "w-auto",
        full: "w-full",
      },
      shape: {
        rounded: "rounded-md",
        pill: "rounded-full",
        sharp: "rounded-none",
        brand: "rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      width: "auto",
      shape: "brand",
    },
    compoundVariants: [
      // Ensure icon buttons are always square
      { size: "icon", class: "w-10 h-10" },
      { size: "iconSm", class: "w-8 h-8" },
      { size: "iconLg", class: "w-12 h-12" },
    ],
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      width,
      shape,
      asChild = false,
      loading = false,
      loadingText = "Loading…",
      icon,
      iconPosition = "left",
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, width, shape }),
          loading && "pointer-events-none opacity-75",
          className,
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {loadingText && <span className="ml-2">{loadingText}</span>}
          </>
        ) : (
          <>
            {icon && iconPosition === "left" && (
              <span className="mr-2 inline-flex shrink-0">{icon}</span>
            )}
            {children}
            {icon && iconPosition === "right" && (
              <span className="ml-2 inline-flex shrink-0">{icon}</span>
            )}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
